<template>
  <div class="q-px-sm">
    <slot
      v-if="!hasNoProgramMods || !hideEmpty"
      name="header"
    />
    <div
      v-if="hasNoProgramMods && !hideEmpty"
    >
      {{ noActivitiesLabel || $t('activityFeed.textNoActivities') }}
    </div>
    <template
      v-else
    >
      <q-timeline
        layout="dense"
        color="primary"
      >
        <template
          v-for="(activityList, formattedDate) in activityGroups"
          :key="formattedDate"
        >
          <q-timeline-entry
            v-if="dense"
            heading
            tag="h6"
          >
            {{ formattedDate }}
          </q-timeline-entry>
          <q-timeline-entry
            v-for="activity in activityList"
            :key="activity.id"
            :subtitle="getActivitySubtitle(activity)"
            :title="getActivityTitle(activity)"
            :icon="getActivityIcon(activity)"
            :color="getActivityColor(activity)"
            :class="{'cursor-pointer': clickable}"
            @click="onClickActivity(activity)"
          >
            <div
              v-if="contentHtmlById[activity.id]"
              class="editor-content"
              v-html="contentHtmlById[activity.id]"
            />
            <template v-else>
              {{ getActivityContent(activity) }}
            </template>
          </q-timeline-entry>
        </template>
      </q-timeline>
      <div
        v-if="hasMoreActivities && !isLoading"
        class="col-12 row justify-center"
      >
        <q-btn
          :label="$t('activityFeed.btnLoadMore')"
          flat
          dense
          color="primary"
          @click="onLoadMoreActivities"
        />
      </div>
    </template>
    <div
      v-if="isLoading"
    >
      <q-timeline
        layout="dense"
        color="primary"
      >
        <q-timeline-entry
          v-for="skeleton in 5"
          :key="skeleton"
        >
          <template #title>
            <q-skeleton type="text" />
          </template>
          <template #subtitle>
            <q-skeleton type="text" />
          </template>
          <q-skeleton />
        </q-timeline-entry>
      </q-timeline>
    </div>
  </div>
</template>
<script>
import { chain, concat, filter, find, get, groupBy, isEmpty, map, size, slice } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import ActivitiesDB from 'src/fb/activities-db';
import Activities from 'src/mixins/Activities';
import { auth } from 'src/store/auth';

/**
 * The quantity of constraints we can use in readAll() method without loosing
 * ability to ordering, limiting and slicing data coming from DB
 */
const CONSTRAINTS_LIMIT = 10;

export default {
  name: 'ActivityFeed',

  mixins: [Activities],

  props: {
    events: {
      type: Array,
      required: true,
    },

    filter: {
      type: Array,
      default: () => [],
    },

    hideEmpty: {
      type: Boolean,
      default: false,
    },

    noActivitiesLabel: {
      type: String,
      default: null,
    },

    dense: {
      type: Boolean,
      default: false,
    },

    clickable: {
      type: Boolean,
      default: false,
    },

    clientId: {
      type: String,
      default: null,
    },
  },

  emits: ['click'],

  setup() {
    const { getLocaleDateString, getLocaleTimeString } = useTime();
    return { getLocaleDateString, getLocaleTimeString };
  },

  data() {
    return {
      isLoading: false,
      hasMoreActivities: true,
      activities: [],

      timelineCount: 5,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),

    activityGroups() {
      if (!this.dense) {
        return {
          0: this.activities
            .slice(0, size(this.activities) - (this.hasMoreActivities ? 1 : 0)),
        };
      }
      return chain(this.activities)
        .slice(0, size(this.activities) - (this.hasMoreActivities ? 1 : 0))
        .map((activity) => ({
          formattedDate: this.getLocaleDateString(activity.createTimestamp, {
            options: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
          }),
          ...activity,
        }))
        .groupBy('formattedDate')
        .value();
    }, // activityGroups()

    // Computed once per load, so each note is sanitized once rather than on every render.
    contentHtmlById() {
      return Object.fromEntries(
        this.activities.map((activity) => [activity.id, this.getActivityContentHtml(activity)]),
      );
    }, // contentHtmlById()

    hasNoProgramMods() {
      return isEmpty(this.activities) && !this.isLoading;
    }, // hasNoProgramMods()

    isFilterWithBigConstraint() {
      return !!(find(this.filter, (constraint) => get(constraint, ['2', 'length']) > CONSTRAINTS_LIMIT));
    }, // isFilterWithBigConstraint()

  },

  watch: {
    'currentClient.id': {
      immediate: true,
      handler(currentClientId) {
        if (!currentClientId && !this.clientId) {
          this.activities = [];
          this.hasMoreActivities = false;
          return;
        }
        this.reloadActivities();
      },
    },
    clientId(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (!this.clientId) {
          this.activities = [];
          this.hasMoreActivities = false;
          return;
        }
        this.reloadActivities();
      }
    },
  },

  methods: {

    onLoadMoreActivities() {
      this.loadActivities();
    }, // onLoadMoreActivities()

    onClickActivity(activity) {
      this.$emit('click', activity);
    }, // onClickActivity()

    async reloadActivities() {
      this.activities = [];
      this.hasMoreActivities = false;
      this.loadActivities();
    }, // reloadActivities()

    async loadActivities() {
      this.isLoading = true;

      try {
        const activitiesDb = new ActivitiesDB();
        let activities = await Promise.all(map(this.events, (event) => {
          const client = this.clientId || this.currentClient.id;
          return activitiesDb.readAll(
            [
              ['client', '==', client],
              ['event', '==', event],
              ...this.filter,
            ],
            'createTimestamp',
            'desc',
            this.timelineCount + 1,
            chain(this.activities).last().get('createTimestamp', null).value(),
          );
        }));

        activities = chain(activities)
          .flatten()
          .orderBy([(activity) => activity.createTimestamp.toDate()], ['desc'])
          .value();

        if (this.isFilterWithBigConstraint) {
          const groupedActivities = groupBy(this.activities, 'id');
          activities = filter(activities, (activity) => !(groupedActivities[get(activity, 'id')]));
        }

        activities = slice(activities, 0, this.timelineCount + 1);

        this.hasMoreActivities = size(activities) >= (this.timelineCount + 1);
        this.activities = concat(this.activities, activities);
      } finally {
        this.isLoading = false;
      }
    }, // refreshActivities()

    formatActivityTimestamp(timestamp) {
      return `${this.getLocaleDateString(timestamp, {
        options: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
      })} ${this.getLocaleTimeString(timestamp, { options: { hour: 'numeric', minute: '2-digit' } })}`;
    }, // formatActivityTimestamp()

    getActivityTitle(activity) {
      if (this.dense) {
        return null;
      }
      return get(activity, ['eventInfo', 'userName']);
    }, // getActivityTitle()

    getActivitySubtitle(activity) {
      if (this.dense) {
        const prefix = `${this.getLocaleTimeString(activity.createTimestamp, {
          options: { hour: 'numeric', minute: '2-digit' },
        })} - `;

        return this.getActivityEventLabel(activity, prefix);
      }
      return this.formatActivityTimestamp(activity.createTimestamp);
    }, // getActivitySubtitle()
  },
};
</script>
